import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageCircleQuestion, Pencil, Trash2, Loader2, Check, X, Sparkles, RotateCcw } from "lucide-react";

// Admin review of AI explanations students requested. Edits become the cached answer.
export default function QaReview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/theory/qa"); setItems(r.data || []); }
    catch { toast.error("Failed to load Q&A"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (it) => { setEditId(it.id); setDraft(it.explanation || ""); };
  const saveEdit = async (it) => {
    if (!draft.trim()) { toast.error("Explanation cannot be empty"); return; }
    setSaving(true);
    try {
      await api.put(`/theory/qa/${it.id}`, { explanation: draft });
      toast.success("Answer updated — this is now the cached reply"); setEditId(null); load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const remove = async (it) => {
    if (!window.confirm("Delete this Q&A? The next student ask will regenerate it via AI.")) return;
    try { await api.delete(`/theory/qa/${it.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-lime-500" /></div>;

  return (
    <div className="space-y-4" data-testid="qa-review">
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
        <MessageCircleQuestion className="h-5 w-5 text-lime-500" />
        <p className="text-sm text-slate-600">Questions students asked the AI to explain. Edit an answer to correct it — your edited version becomes the cached reply reused for everyone (and saves AI credits).</p>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-16" data-testid="qa-empty">No AI explanations requested yet. When a student clicks “Ask AI for a more detailed explanation” on a wrong answer, it appears here.</p>
      ) : (
        items.map((it) => (
          <div key={it.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`qa-item-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-slate-800">{it.prompt}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{it.lesson_title}{it.element_title ? ` · ${it.element_title}` : ""}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {it.edited ? <Badge className="border bg-lime-100 text-lime-700 border-lime-200">Edited</Badge> : <Badge className="border bg-sky-100 text-sky-700 border-sky-200 gap-1"><Sparkles className="h-3 w-3" /> AI</Badge>}
                <Badge variant="outline" className="text-slate-400 gap-1"><RotateCcw className="h-3 w-3" /> {it.ask_count || 1}</Badge>
              </div>
            </div>
            {it.correct_text && <p className="text-xs text-slate-500 mt-2"><span className="font-semibold">Correct:</span> {it.correct_text}</p>}

            {editId === it.id ? (
              <div className="mt-3">
                <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={6} className="text-sm" data-testid={`qa-edit-${it.id}`} />
                <div className="flex gap-2 mt-2">
                  <Button size="sm" className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" onClick={() => saveEdit(it)} disabled={saving} data-testid={`qa-save-${it.id}`}><Check className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save answer"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditId(null)}><X className="h-4 w-4 mr-1.5" /> Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid={`qa-answer-${it.id}`}>{it.explanation}</div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid={`qa-edit-btn-${it.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit</Button>
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => remove(it)} data-testid={`qa-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}
